import { Download, Eye, LockKeyhole } from "lucide-react";
import { useState } from "react";
import { MediaLightbox } from "@/components/AttachmentPreviews";
import EventMediaHeading from "@/components/EventMediaHeading";
import MembershipLockNotice from "@/components/MembershipLockNotice";
import FileTypeBadge from "@/components/FileTypeBadge";
import { FileText } from "lucide-react";
import {
  EVENT_DOCUMENT_CONTENT_TYPE,
  eventDocumentDownloadHref,
  eventDocumentViewHref,
  formatEventDocumentSize,
} from "@shared/eventDocuments";

export type EventDocumentSummary = {
  id: number;
  title: string;
  fileName: string;
  sizeBytes: number;
};

/**
 * The documents attached to an event, on its public page.
 *
 * Everyone sees the list; paid members get View and Download on each row and
 * everyone else gets the same rows locked, with the one sentence that explains why
 * and where to go. Showing the titles to a visitor who cannot open them is
 * deliberate: a deck and a worksheet are a concrete reason to join, and a hidden
 * list is not.
 *
 * View opens the file in the same lightbox the flyers use — a sandboxed frame with
 * scripting denied — served inline by the download route. Download is a plain
 * anchor to the attachment variant, so the browser's download manager takes over
 * and no page script ever handles the bytes.
 */
export default function EventDocumentList({
  documents,
  canDownload,
  isSignedIn,
  eventSlug,
}: {
  documents: EventDocumentSummary[];
  canDownload: boolean;
  isSignedIn: boolean;
  eventSlug: string;
}) {
  const [viewing, setViewing] = useState<EventDocumentSummary | null>(null);

  if (documents.length === 0) return null;

  const rowStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: ".8rem",
    padding: ".75rem 1rem",
    borderRadius: ".7rem",
    border: "1px solid rgba(255,255,255,.1)",
    background: "rgba(255,255,255,.03)",
    color: "#F1F5F9",
  };

  const actionStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: ".35rem",
    minHeight: 36,
    padding: ".4rem .7rem",
    borderRadius: ".5rem",
    border: "1px solid rgba(125,234,255,.3)",
    background: "rgba(125,234,255,.08)",
    color: "#7DEAFF",
    fontFamily: "'Space Grotesk', sans-serif",
    fontSize: "var(--chi-body-compact)",
    fontWeight: 700,
    textDecoration: "none",
    cursor: "pointer",
    flex: "0 0 auto",
  };

  return (
    <div className="event-document-list">
      <EventMediaHeading icon={FileText}>Event Docs</EventMediaHeading>
      <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: ".6rem" }}>
        {documents.map(document => {
          const meta = (
            <span style={{ minWidth: 0, flex: "1 1 auto", display: "grid" }}>
              <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "var(--chi-body-compact)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{document.title}</span>
              <span style={{ color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-dense)" }}>{formatEventDocumentSize(document.sizeBytes)}</span>
            </span>
          );
          return (
            <li key={document.id}>
              {canDownload ? (
                <div style={rowStyle}>
                  <FileTypeBadge fileName={document.fileName} size={30} />
                  {meta}
                  <button type="button" onClick={() => setViewing(document)} style={actionStyle} aria-label={`View ${document.title}`}>
                    <Eye size={16} aria-hidden="true" /> View
                  </button>
                  <a href={eventDocumentDownloadHref(document.id)} style={actionStyle} aria-label={`Download ${document.title}`}>
                    <Download size={16} aria-hidden="true" /> Download
                  </a>
                </div>
              ) : (
                <div style={{ ...rowStyle, color: "#CBD5E1" }} aria-label={`${document.title} — available to paid members`}>
                  <span style={{ opacity: .55, display: "flex" }}><FileTypeBadge fileName={document.fileName} size={30} /></span>
                  {meta}
                  <LockKeyhole size={18} aria-hidden="true" style={{ color: "#64748B", flex: "0 0 auto" }} />
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {!canDownload && (
        <div style={{ marginTop: ".8rem" }}>
          <MembershipLockNotice what="Event Docs" isSignedIn={isSignedIn} returnTo={`/events/${eventSlug}`} />
        </div>
      )}
      {viewing && (
        <MediaLightbox
          attachment={{ url: eventDocumentViewHref(viewing.id), label: viewing.title, contentType: EVENT_DOCUMENT_CONTENT_TYPE }}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
}
